import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, EmptyState, Icon, Skeleton } from '@vault/ui';
import type { DupGroup, DupGroupMember } from '@vault/shared';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { useT } from '../i18n/index.js';
import {
  ProUpsellModal,
  browserColor,
  browserLabel,
  favVar,
  hashIndex,
  hostname,
  initial,
  isApiStatus,
  errMessage,
  useToasts,
} from './_vault/shared.js';

/** ms of RAM freed per duplicate tab we close — a rough browser-tab average.
 *  Derived estimate over real closable-tab count, never a hardcoded total. */
const MB_PER_TAB = 40;

function memberId(m: DupGroupMember): string {
  return `${m.deviceId}::${m.tabKey}`;
}

/** Group members by device into the { deviceId, tabKeys } command shape. */
function buildCommands(members: DupGroupMember[]): Array<{ deviceId: string; tabKeys: string[] }> {
  const byDevice = new Map<string, string[]>();
  for (const m of members) {
    const arr = byDevice.get(m.deviceId) ?? [];
    arr.push(m.tabKey);
    byDevice.set(m.deviceId, arr);
  }
  return Array.from(byDevice, ([deviceId, tabKeys]) => ({ deviceId, tabKeys }));
}

export function DuplicatesPage() {
  const qc = useQueryClient();
  const { isPro } = useAuth();
  const toasts = useToasts();
  const { t, plural } = useT();

  const query = useQuery({ queryKey: ['dedup', 'live'], queryFn: () => api.dedup.live() });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [upsell, setUpsell] = useState(false);

  // only groups with an actual duplicate (>1 open copy)
  const dupGroups: DupGroup[] = useMemo(
    () => (query.data?.groups ?? []).filter((g) => g.members.length > 1),
    [query.data],
  );

  // every closable copy = all members except the newest (index 0, which we keep)
  const closableIds = useMemo(
    () => dupGroups.flatMap((g) => g.members.slice(1)).map(memberId),
    [dupGroups],
  );
  const allClosable = useMemo(() => dupGroups.flatMap((g) => g.members.slice(1)), [dupGroups]);

  // pre-select every closable copy; reset only when the underlying set changes
  const signature = closableIds.join('|');
  useEffect(() => {
    setSelected(new Set(closableIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const closeMut = useMutation({
    mutationFn: (members: DupGroupMember[]) => api.dedup.close(buildCommands(members)),
    onSuccess: (_res, members) => {
      qc.invalidateQueries({ queryKey: ['dedup', 'live'] });
      qc.invalidateQueries({ queryKey: ['dedup', 'summary'] });
      toasts.show({
        tone: 'success',
        title: t('dupes.toast.closed', { copies: plural('unit.copies', members.length) }),
        description: t('dupes.toast.closedBody'),
      });
    },
    onError: (err) => {
      if (isApiStatus(err, 402)) {
        setUpsell(true);
        return;
      }
      toasts.show({ tone: 'danger', title: 'Could not close tabs', description: errMessage(err) });
    },
  });

  function runClose(members: DupGroupMember[]) {
    if (members.length === 0 || closeMut.isPending) return;
    if (!isPro) {
      setUpsell(true);
      return;
    }
    closeMut.mutate(members);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleCollapse(urlNorm: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(urlNorm)) next.delete(urlNorm);
      else next.add(urlNorm);
      return next;
    });
  }

  const selectedCount = closableIds.filter((id) => selected.has(id)).length;
  const allSelected = closableIds.length > 0 && selectedCount === closableIds.length;
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(closableIds));
  }
  function closeSelected() {
    runClose(allClosable.filter((m) => selected.has(memberId(m))));
  }

  // ── summary numbers (all derived from live data) ──
  const closableCount = closableIds.length;
  const browserCount = useMemo(() => {
    const set = new Set<string>();
    for (const g of dupGroups) for (const m of g.members) set.add(m.browser);
    return set.size;
  }, [dupGroups]);
  const ramMb = closableCount * MB_PER_TAB;
  const ramLabel = ramMb >= 1024 ? `${(ramMb / 1024).toFixed(1)} GB` : `${ramMb} MB`;

  return (
    <div className="dupc">
      <style>{CSS}</style>

      <div className="dupc-top">
        <h1 className="dupc-h1">{t('dupes.title')}</h1>
        {!query.isLoading && !query.isError && dupGroups.length > 0 && (
          <>
            <span className="dupc-summary">
              <span style={{ color: 'var(--amber-ink)', fontWeight: 600 }}>{t('dupes.summary.duplicates', { count: closableCount })}</span>
              <span className="dupc-dotsep" />
              <span>{plural('unit.browsers', browserCount).toUpperCase()}</span>
              <span className="dupc-dotsep" />
              <span>{t('dupes.summary.ram', { ram: ramLabel })}</span>
            </span>
            <div className="dupc-actions">
              <button
                type="button"
                className="dupc-btn sec"
                disabled={selectedCount === 0 || closeMut.isPending}
                onClick={closeSelected}
              >
                {t('dupes.closeSelected', { count: selectedCount })}
              </button>
              <button
                type="button"
                className="dupc-btn pri"
                disabled={closableCount === 0 || closeMut.isPending}
                onClick={() => runClose(allClosable)}
              >
                <Icon name="check" size={13} color="#fff" strokeWidth={1.8} />
                {t('dupes.keepNewest')}
              </button>
            </div>
          </>
        )}
      </div>

      {query.isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={132} radius={14} />
          ))}
        </div>
      )}

      {query.isError && (
        <div className="dupc-error">
          <span style={{ fontSize: 13, fontWeight: 600 }}>{t('dupes.error.title')}</span>
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{errMessage(query.error)}</span>
          <button type="button" className="dupc-btn sec" onClick={() => query.refetch()}>Try again</button>
        </div>
      )}

      {!query.isLoading && !query.isError && dupGroups.length === 0 && (
        <div style={{ maxWidth: 460, margin: '48px auto' }}>
          <EmptyState
            dashed={false}
            icon={<Icon name="layers" size={18} color="var(--text-3)" />}
            title="No duplicates across your browsers"
            description="Every open tab is unique right now. When the same page is open in two places, we’ll group the copies here so you can keep the newest."
          />
        </div>
      )}

      {!query.isLoading && !query.isError && dupGroups.length > 0 && (
        <>
          <div className="dupc-sub">
            <button
              type="button"
              className={`dupc-check${allSelected ? ' on' : ''}`}
              onClick={toggleAll}
              aria-label={allSelected ? 'Clear selection' : 'Select all'}
            >
              {allSelected && <Icon name="check" size={9} color="#fff" strokeWidth={2.4} />}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {selectedCount} of {closableCount} selected
            </span>
            <span className="dupc-sub-note">Grouped by url · newest copy is always kept</span>
          </div>

          {dupGroups.map((g) => {
            const isCollapsed = collapsed.has(g.urlNorm);
            const title = g.members[0]?.title || hostname(g.url);
            const closable = g.members.slice(1);
            return (
              <div key={g.urlNorm} className={`dupc-group${isCollapsed ? ' collapsed' : ''}`}>
                <div className="dupc-ghead">
                  <span className="dupc-fav" style={{ background: favVar(hashIndex(g.urlNorm, 8)) }}>
                    {initial(title)}
                  </span>
                  <span className="dupc-gtitle">{title}</span>
                  <span className="dupc-gdom">{hostname(g.url)}</span>
                  <span style={{ marginLeft: 'auto' }}>
                    <Badge tone="warn" pill={false}>{g.members.length} copies</Badge>
                  </span>
                  <button
                    type="button"
                    className="dupc-keep"
                    disabled={closeMut.isPending}
                    onClick={() => runClose(closable)}
                    title="Keep newest, close rest"
                  >
                    <Icon name="check" size={12} />
                    Keep newest
                  </button>
                  <button
                    type="button"
                    className="dupc-chev"
                    onClick={() => toggleCollapse(g.urlNorm)}
                    aria-label={isCollapsed ? 'Expand group' : 'Collapse group'}
                  >
                    <Icon name="chevron-down" size={11} />
                  </button>
                </div>

                {!isCollapsed &&
                  g.members.map((m, i) => {
                    const isClosable = i > 0;
                    const mid = memberId(m);
                    const sel = selected.has(mid);
                    return (
                      <div key={mid} className={`dupc-row${isClosable && sel ? ' sel' : ''}`}>
                        {isClosable ? (
                          <button
                            type="button"
                            className={`dupc-check${sel ? ' on' : ''}`}
                            onClick={() => toggle(mid)}
                            aria-label={sel ? 'Deselect copy' : 'Select copy'}
                          >
                            {sel && <Icon name="check" size={9} color="#fff" strokeWidth={2.4} />}
                          </button>
                        ) : (
                          <span className="dupc-check ghost" aria-hidden />
                        )}
                        <span className="dupc-br">
                          <i className="dupc-brdot" style={{ background: browserColor(m.browser) }} />
                          {browserLabel(m.browser)}
                        </span>
                        <span className="dupc-meta dev">{(m.deviceName || 'Unknown device').toUpperCase()}</span>
                        {isClosable ? (
                          <button
                            type="button"
                            className="dupc-close"
                            style={{ marginLeft: 'auto' }}
                            disabled={closeMut.isPending}
                            onClick={() => runClose([m])}
                          >
                            Close
                          </button>
                        ) : (
                          <span style={{ marginLeft: 'auto' }}>
                            <Badge tone="success" pill={false}>Newest · keep</Badge>
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </>
      )}

      <ProUpsellModal
        open={upsell}
        onClose={() => setUpsell(false)}
        title="Auto-close is a Pro feature"
        body="Free shows you every duplicate across your browsers. Pro closes the extra copies in one click and always keeps the newest — every copy stays saved in your vault."
      />
      {toasts.node}
    </div>
  );
}

const CSS = `
.dupc { padding: 24px 28px; max-width: 1140px; margin: 0 auto; display: flex; flex-direction: column; gap: 16px; }
.dupc-top { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.dupc-h1 { font-family: var(--font-display); font-size: 20px; font-weight: 600; letter-spacing: -0.025em; margin: 0; }
.dupc-summary { display: inline-flex; align-items: center; gap: 14px; border: 1px solid var(--line); border-radius: 999px; padding: 7px 16px; font-family: var(--font-mono); font-size: 10.5px; color: var(--text-2); white-space: nowrap; }
.dupc-dotsep { width: 4px; height: 4px; border-radius: 50%; background: var(--line-strong); flex-shrink: 0; }
.dupc-actions { margin-left: auto; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.dupc-btn { display: inline-flex; align-items: center; gap: 8px; border-radius: 999px; padding: 9px 16px; font-family: var(--font-body); font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; border: 1px solid transparent; }
.dupc-btn.sec { border-color: var(--line-strong); color: var(--text-2); background: transparent; }
.dupc-btn.sec:hover { background: var(--surface-1); }
.dupc-btn.sec:disabled { opacity: .5; cursor: default; }
.dupc-btn.pri { background: var(--primary); color: #fff; }
.dupc-btn.pri:hover { background: var(--primary-hover); }
.dupc-btn.pri:disabled { opacity: .6; cursor: default; }
.dupc-sub { display: flex; align-items: center; gap: 10px; padding: 0 2px; }
.dupc-sub-note { font-family: var(--font-mono); font-size: 10px; color: var(--text-3); margin-left: auto; text-align: right; }
.dupc-group { background: var(--surface-1); border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
.dupc-ghead { display: flex; align-items: center; gap: 11px; padding: 13px 16px; background: var(--surface-0); border-bottom: 1px solid var(--line); }
.dupc-group.collapsed .dupc-ghead { border-bottom: none; }
.dupc-fav { width: 20px; height: 20px; border-radius: 5px; display: inline-flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 600; color: #fff; flex-shrink: 0; }
.dupc-gtitle { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.dupc-gdom { font-family: var(--font-mono); font-size: 10px; color: var(--text-3); white-space: nowrap; flex-shrink: 0; }
.dupc-keep { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 600; color: var(--text-2); border: 1px solid var(--line-strong); border-radius: 999px; padding: 5px 12px; cursor: pointer; background: transparent; white-space: nowrap; flex-shrink: 0; }
.dupc-keep:hover { background: var(--surface-1); }
.dupc-keep:disabled { opacity: .5; cursor: default; }
.dupc-chev { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 8px; border: 1px solid var(--line-strong); color: var(--text-2); cursor: pointer; background: transparent; flex-shrink: 0; }
.dupc-chev svg { transition: transform .2s var(--ease); }
.dupc-group.collapsed .dupc-chev svg { transform: rotate(-90deg); }
.dupc-row { display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-bottom: 1px solid var(--line); }
.dupc-row:last-child { border-bottom: none; }
.dupc-row.sel { background: var(--primary-tint); }
.dupc-check { width: 16px; height: 16px; border-radius: 5px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; cursor: pointer; border: 1.5px solid var(--line-strong); background: transparent; padding: 0; }
.dupc-check.on { background: var(--primary); border-color: var(--primary); }
.dupc-check.ghost { cursor: default; opacity: .55; }
.dupc-br { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; width: 150px; flex-shrink: 0; }
.dupc-brdot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.dupc-meta { font-family: var(--font-mono); font-size: 10px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
.dupc-close { display: inline-flex; align-items: center; font-size: 11.5px; font-weight: 600; color: var(--danger); border: 1px solid var(--danger-line); border-radius: 999px; padding: 4px 12px; cursor: pointer; background: transparent; white-space: nowrap; flex-shrink: 0; }
.dupc-close:hover { background: var(--danger-tint); }
.dupc-close:disabled { opacity: .5; cursor: default; }
.dupc-error { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; background: var(--surface-1); border: 1px solid var(--line); border-radius: 14px; padding: 20px; }
@media (max-width: 720px) {
  .dupc { padding: 16px; }
  .dupc-actions { margin-left: 0; width: 100%; }
  .dupc-actions .dupc-btn { flex: 1; justify-content: center; }
  .dupc-br { width: auto; flex: 1; }
  .dupc-meta.dev { display: none; }
  .dupc-keep { display: none; }
}
`;
